// Шаблон первого сообщения для сценария "Уточнение опыта".
// Используется как дефолт при создании рассылки из откликов — рекрутер
// может отредактировать его в окне настроек рассылки перед запуском.
export const CLARIFY_EXPERIENCE_TEMPLATE = `{{candidate_name}}, здравствуйте!

Спасибо Вам за отклик на вакансию {{position}}!

Ваше резюме показалось нам очень интересным, но мы хотели бы уточнить у вас опыт по важным для нас требованиям.

Вы не против ответить на вопросы нашего ИИ помощника?

Отвечайте, пожалуйста, на вопросы подробно, хотя бы 2-3 предложения. Ответы "Да, работал" или "Опыт 5 лет" не позволят нам полноценно оценить ваш опыт.

Спасибо!
{{recruiter_name}}`;

function normalizeContactValue(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  const phoneParts = value.country && value.city && value.number
    ? `${value.country}${value.city}${value.number}`
    : null;
  const normalized = phoneParts || value.formatted || value.number || value.email || value.value || value.username || null;
  return typeof normalized === 'string' && normalized.trim() ? normalized.trim() : null;
}

function normalizeContactEntryValue(contact: any): string | null {
  return normalizeContactValue(contact?.contact_value)
    || normalizeContactValue(contact?.value)
    || normalizeContactValue(contact?.url)
    || normalizeContactValue(contact?.links?.web)
    || normalizeContactValue(contact?.links?.android)
    || normalizeContactValue(contact?.links?.ios);
}

function phoneContactPriority(contact: any): number {
  switch (contact?.type?.id) {
    case 'cell':
      return 0;
    case 'phone':
      return 1;
    case 'home':
      return 2;
    case 'work':
      return 3;
    default:
      return 99;
  }
}

function normalizeNamePart(value: any): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().replace(/\s+/g, ' ');
  return normalized || null;
}

// Извлекаем телефон/мессенджер-контакты из массива контактов резюме HH.
// Формат массива согласован с тем, что отдаёт hr-proxy в /resume.
export function extractMessengerContacts(contacts: any[] | undefined | null): {
  phone: string | null;
  email: string | null;
  telegram: string | null;
  whatsapp: string | null;
} {
  const list = Array.isArray(contacts) ? contacts : [];
  const phoneContacts = list
    .filter((c: any) => phoneContactPriority(c) < 99)
    .sort((a: any, b: any) => phoneContactPriority(a) - phoneContactPriority(b));
  const phone = phoneContacts
    .map((c: any) => normalizeContactEntryValue(c))
    .find(Boolean) || null;
  const emailContact = list.find((c: any) => c.type?.id === 'email');
  const email = normalizeContactEntryValue(emailContact);

  let telegram: string | null = normalizeContactEntryValue(list.find((c: any) => c.type?.id === 'telegram'));
  let whatsapp: string | null = normalizeContactEntryValue(list.find((c: any) => c.type?.id === 'whatsapp'));
  for (const c of list) {
    if (Array.isArray(c.preferred)) {
      if (c.preferred.some((p: any) => p.id === 'telegram') && !telegram) {
        telegram = normalizeContactEntryValue(c) || phone;
      }
      if (c.preferred.some((p: any) => p.id === 'whatsapp') && !whatsapp) {
        whatsapp = normalizeContactEntryValue(c) || phone;
      }
    }
  }
  return { phone, email, telegram, whatsapp };
}

export interface ClarifyExperienceCandidateInput {
  resume: any;
  fullResume?: any;
  campaignCandidate?: any;
  response?: any;
  responseId: string | number;
  resumeUrl?: string;
  relevanceFields?: Record<string, any>;
  source?: 'hh' | 'avito';
  avitoChatId?: string | null;
  avitoApplicationId?: string | number | null;
}

// Собираем объект кандидата для запроса createCampaign.
// Внутренние контакты тянем из campaignCandidate, а presentation-only resume/fullResume используем как fallback.
export function buildClarifyExperienceCandidate(input: ClarifyExperienceCandidateInput) {
  const { resume, fullResume, campaignCandidate, response, responseId, resumeUrl, relevanceFields } = input;
  const source = input.source || 'hh';
  const resumeId = resume?.id;
  const firstName = normalizeNamePart(campaignCandidate?.firstName)
    || normalizeNamePart(campaignCandidate?.first_name)
    || normalizeNamePart(fullResume?.first_name)
    || normalizeNamePart(resume?.first_name);
  const lastName = normalizeNamePart(campaignCandidate?.lastName)
    || normalizeNamePart(campaignCandidate?.last_name)
    || normalizeNamePart(fullResume?.last_name)
    || normalizeNamePart(resume?.last_name);
  const middleName = normalizeNamePart(campaignCandidate?.middleName)
    || normalizeNamePart(campaignCandidate?.middle_name)
    || normalizeNamePart(fullResume?.middle_name)
    || normalizeNamePart(resume?.middle_name);
  const fullName = normalizeNamePart(campaignCandidate?.fullName)
    || normalizeNamePart(campaignCandidate?.full_name)
    || normalizeNamePart(fullResume?.full_name)
    || normalizeNamePart(fullResume?.name)
    || normalizeNamePart(resume?.full_name)
    || normalizeNamePart(resume?.name);
  const nameFallbackTitle = normalizeNamePart(fullResume?.title) || normalizeNamePart(resume?.title);
  const hasNameParts = Boolean(firstName || lastName || middleName);
  const hiddenName = !hasNameParts && Boolean(nameFallbackTitle) && (!fullName || fullName === nameFallbackTitle);
  const candidateName = [firstName, lastName].filter(Boolean).join(' ') || fullName || nameFallbackTitle || 'Кандидат';
  const contacts = fullResume?.contact || resume?.contact || [];
  const extracted = extractMessengerContacts(contacts);
  const phone = source === 'avito'
    ? (
        normalizeContactValue(campaignCandidate?.phone)
        || normalizeContactValue(response?.contacts?.phones?.[0]?.value)
      )
    : (
        normalizeContactValue(campaignCandidate?.phone)
        || extracted.phone
        || normalizeContactValue(fullResume?.phone)
        || normalizeContactValue(resume?.phone)
        || normalizeContactValue(response?.phone)
      );
  const email = source === 'avito'
    ? normalizeContactValue(campaignCandidate?.email)
    : (
        normalizeContactValue(campaignCandidate?.email)
        || extracted.email
        || normalizeContactValue(fullResume?.email)
        || normalizeContactValue(resume?.email)
        || normalizeContactValue(response?.email)
      );
  const telegram = source === 'avito'
    ? normalizeContactValue(campaignCandidate?.telegram)
    : normalizeContactValue(campaignCandidate?.telegram) || extracted.telegram;
  const whatsapp = source === 'avito'
    ? normalizeContactValue(campaignCandidate?.whatsapp)
    : normalizeContactValue(campaignCandidate?.whatsapp) || extracted.whatsapp;
  const avitoChatId = normalizeContactValue(input.avitoChatId)
    || normalizeContactValue(campaignCandidate?.avitoChatId)
    || normalizeContactValue(response?.contacts?.chat?.value);
  const campaignResumeUrl = normalizeContactValue(campaignCandidate?.resumeUrl)
    || normalizeContactValue(campaignCandidate?.resume_url)
    || resumeUrl;
  const messengerContacts = source === 'avito'
    ? {
        telegram,
        whatsapp,
        phone,
        email,
        max: null,
        avitoChatId,
        avitoApplicationId: normalizeContactValue(input.avitoApplicationId) || String(responseId),
        hhResumeId: null,
      }
    : {
        hhResumeId: resumeId,
        hhNegotiationId: String(responseId),
        telegram,
        whatsapp,
        phone,
        email,
      };

  return {
    candidate: {
      externalId: resumeId,
      ...(source === 'avito' ? { source: 'AVITO', sourceExternalId: resumeId } : {}),
      name: candidateName,
      firstName: firstName || null,
      lastName: lastName || null,
      middleName: middleName || null,
      hiddenName,
      email,
      ...(campaignResumeUrl ? { resumeUrl: campaignResumeUrl } : {}),
      messengerContacts,
      ...(relevanceFields || {}),
    },
    candidateName,
  };
}

export function hasClarifyCandidateContact(candidate: any): boolean {
  const hhResumeId = normalizeContactValue(candidate?.messengerContacts?.hhResumeId);
  const hhNegotiationId = normalizeContactValue(candidate?.messengerContacts?.hhNegotiationId);
  const avitoChatId = normalizeContactValue(candidate?.messengerContacts?.avitoChatId);
  return Boolean(
    normalizeContactValue(candidate?.messengerContacts?.phone)
    || normalizeContactValue(candidate?.messengerContacts?.email)
    || normalizeContactValue(candidate?.email)
    || avitoChatId
    || (hhResumeId && hhNegotiationId)
  );
}

export function isOpenedContactsReadyForClarify(opened: any, estaffVacancyId?: string | number | null): boolean {
  const resume = opened?.resume || opened?.data || null;
  const hasOpenedContacts = Boolean(
    opened?.hasContacts
    || (Array.isArray(resume?.contact) && resume.contact.length > 0)
  );
  const estaffSyncReady = !estaffVacancyId || opened?.estaffSync?.action === 'queued';
  return hasOpenedContacts && estaffSyncReady;
}

export interface ClarifyExperienceCampaignParams {
  candidates: any[];
  vacancyTitle: string;
  vacancyDescription?: string;
  vacancyCompanyDescription?: string;
  hhVacancyId?: string | number;
  avitoVacancyId?: string | number;
  estaffVacancyId?: string | number;
  positionId?: string | number;
  recruiterName: string;
  recruiterId?: string;
  aiScreeningEnabled?: boolean;
  hrInterviewEnabled?: boolean;
  campaignName: string;
  source?: 'hh' | 'avito';
  campaignSettings?: Record<string, any>;
}

// Формируем payload для createCampaign. Сценарий жёстко = CLARIFY_EXPERIENCE,
// чтобы на бэке ai-recruiter подхватился ClarifyExperienceScenario.
// Возвращаемый тип — any: типы Campaign в фронте не описывают scenarioType/candidates,
// а перегружать их ради одного эндпоинта не хочется.
export function buildClarifyExperienceCampaignData(params: ClarifyExperienceCampaignParams): any {
  const {
    candidates,
    vacancyTitle,
    vacancyDescription,
    vacancyCompanyDescription,
    hhVacancyId,
    avitoVacancyId,
    estaffVacancyId,
    positionId,
    recruiterName,
    recruiterId,
    aiScreeningEnabled,
    hrInterviewEnabled,
    campaignName,
    source = 'hh',
    campaignSettings,
  } = params;
  const enabledMessengers = source === 'avito'
    ? ['AVITO']
    : (hhVacancyId ? ['HH'] : []);
  const searchFilters = source === 'avito'
    ? {
        source: 'avito',
        context: 'responses',
        ...(avitoVacancyId ? { avitoVacancyId: String(avitoVacancyId) } : {}),
      }
    : undefined;
  const inheritedAccountSettings = campaignSettings?.accountSettings && typeof campaignSettings.accountSettings === 'object'
    ? campaignSettings.accountSettings
    : {};
  const accountSettings = {
    telegramAccountId: null,
    whatsappAccountId: null,
    maxAccountId: null,
    messengerPriority: enabledMessengers,
    enabledMessengers,
    ...inheritedAccountSettings,
  };

  return {
    name: campaignName,
    targetPosition: vacancyTitle,
    requirements: vacancyDescription || '',
    companyDescription: vacancyCompanyDescription || '',
    hhVacancyId: hhVacancyId || undefined,
    avitoVacancyId: avitoVacancyId || undefined,
    estaffVacancyId: estaffVacancyId || undefined,
    candidateSource: source === 'avito' ? 'AVITO' : undefined,
    searchFilters,
    recruiterName,
    recruiterId: recruiterId || undefined,
    positionId: positionId || undefined,
    scenarioType: 'CLARIFY_EXPERIENCE' as const,
    aiScreeningEnabled: Boolean(aiScreeningEnabled),
    hrInterviewEnabled: hrInterviewEnabled !== false,
    // Лимиты подбираем так, чтобы весь bulk уходил в первый же запуск:
    // берём максимум из дефолта бэка (50 / 20) и числа кандидатов в рассылке.
    // Захардкоженная единица из старого флоу ломала bulk — за день обрабатывался
    // только один кандидат, и UI капал значение через Math.min(daily, max).
    maxActiveDialogs: Math.max(50, candidates.length),
    dailyLimit: Math.max(20, candidates.length),
    ...(campaignSettings || {}),
    accountSettings,
    candidates,
  };
}

// Ключ для location.state при переходе в /recruiter/mailings из откликов.
// MailingsPage читает этот ключ и автоматически открывает таб настроек выбранной рассылки.
export const MAILINGS_NAV_STATE_KEY = 'openCampaign';

export interface MailingsNavState {
  [MAILINGS_NAV_STATE_KEY]: {
    campaignId: string;
    activeTab?: 'settings' | 'mailings';
  };
}
